import { scaleLinear, select } from "d3";
import { produce } from "immer";
import React, { useLayoutEffect, useRef } from "react";
import styled from "styled-components";

import { Cartesian } from "@examspark/types";

import { useGraphDecorator } from "./decorators/drawGraph";
import { useListenPointsDecorator } from "./decorators/drawListenPoints";
import { useShapesDecorator } from "./decorators/drawShapes";

const Wrapper = styled.div`
  background-color: #fff;
  border: solid 1px #ddd;
  user-select: none;

  svg {
    display: block;
  }
`;

const Styled = styled.div`
  display: flex;
  justify-content: center;
`;

interface Props extends Cartesian.Graph {
  mode?: string;
  onChange?: (shapes: Cartesian.Shape[]) => void;
}
/** The interactive area of a number line. */
const NumberLineGraph: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const svgRef = useRef<SVGSVGElement>(null);

  // (height / 2) - offset of axis bar in graph, which is ~5
  const margin = {
    top: props.height / 2 - 5,
    right: 20,
    bottom: 20,
    left: 20,
  };
  const width = props.width - margin.left - margin.right;

  const scale = scaleLinear()
    .domain([props.x.min, props.x.max])
    .range([0, width]);

  const x = {
    scale,
    ...props.x,
  };

  const numberLineDecorator = useGraphDecorator({
    axis: x,
    margin,
  });

  const listenPointsDecorator = useListenPointsDecorator({
    axis: x,
    mode: props.mode,
    margin,
    shapes: props.shapes,
    onChange: props.onChange,
  });

  const shapesDecorator = useShapesDecorator({
    shapes: props.shapes,
    axis: x,
    onMove: (
      shapeIndex: number,
      coordIndex: number,
      coordinate: Cartesian.Coordinate,
    ) => {
      if (props.mode !== undefined) return;

      props.onChange?.(
        produce(props.shapes, (draft) => {
          const shape = draft[shapeIndex];
          if ("coords" in shape) {
            // these shapes cannot be dragged such that they change order, since
            // they are asymmetrically open, and it would be confusing to have
            // a right-open typed shape that was dragged such that the open
            // circle was to the left
            if (
              shape.type === "line-segment-left-open" ||
              shape.type === "line-segment-right-open"
            ) {
              if (coordIndex === 1 && coordinate.x <= shape.coords[0].x) return;
              if (coordIndex === 0 && coordinate.x >= shape.coords[1].x) return;
            }
            // else {
            shape.coords[coordIndex] = coordinate;
            // }
          } else {
            shape.coord = coordinate;
          }

          return draft;
        }),
      );
    },
    onErase: (shapeIndex: number) => {
      if (props.mode !== "erase") return;

      props.onChange?.(
        produce(props.shapes, (draft) => {
          draft.splice(shapeIndex, 1);
        }),
      );
    },
  });

  useLayoutEffect(() => {
    if (!svgRef.current) return;

    const svg = select(svgRef.current);

    // prettier-ignore
    svg
      .call(numberLineDecorator)
    .select(".graph") 
      .call(listenPointsDecorator)

    svg
      .select(".graph")
      .selectAll("g.shapes")
      .data([null])
      .join("g")
      .attr("class", "shapes")
      .selectAll("g.shape")
      .data(props.shapes.map((shape, index) => ({ ...shape, index })))
      .join("g")
      .attr("class", "shape")
      .call(shapesDecorator);

    svg.select(".graph .shape line").style("shape-rendering", "crispedges");
  });

  return (
    <Styled>
      <Wrapper>
        <svg
          ref={svgRef}
          width={props.width}
          height={props.height}
          style={{ shapeRendering: "geometricPrecision" }}
        >
          <defs>
            <marker
              id="arrow"
              markerWidth="26"
              markerHeight="26"
              refX="0"
              refY="6"
              style={{ fill: "#1fa4e1" }}
              orient="auto-start-reverse"
              markerUnits="userSpaceOnUse"
            >
              <path d="M 4 0 L 4 4 L 0 4 L 0 8 L 4 8 L 4 12 L 18 6 L 4 0" />
            </marker>
            <marker
              id="arrow-with-tail"
              markerWidth="13"
              markerHeight="13"
              refX="2"
              refY="3.5"
              style={{ fill: "#696969" }}
              orient="auto-start-reverse"
              markerUnits="userSpaceOnUse"
            >
              <path d="M 6 0 L 6 2.5 L 0 2.5 L 0 4.5 L 6 4.5 L 6 7 L 12 3.5 L 6 0" />
            </marker>
          </defs>
        </svg>
      </Wrapper>
    </Styled>
  );
};

export default NumberLineGraph;
